import { getAwsRum } from "@/lib/awsRum";

interface NetworkInformationLike {
  effectiveType?: string;
}

interface BrandHydrationEventDraft {
  latencyMs: number | null;
  latencyMsPostMount: number | null;
}

const READ_TIMEOUT_MS = 1500;

let registered = false;
let activeObserver: PerformanceObserver | null = null;
let pendingTimeout: number | null = null;

// WHY: normalize FQDN to lowercase and strip trailing dot, matching 32.6's
// extractBrandSlug (guardian-checkin-form/src/lib/branding.ts). A hostname like
// `SMOKETEST.gcheckin.com` or `smoketest.gcheckin.com.` must produce the same slug
// as 32.6 emits to Sentry, otherwise AC 18 calibration's slug-join breaks.
function normalizeHostname(hostname: string): string {
  return hostname.toLowerCase().replace(/\.$/, "");
}

function extractSlug(hostname: string): string {
  // WHY: same hostname-resolution shape as 32.6's extractBrandSlug — slug is the
  // leftmost subdomain only when total parts === 3 AND the apex is gcheckin.com.
  // A two-part hostname (apex gcheckin.com) emits "default" so the SLI dashboard
  // can compare branded vs. default-domain hydration. The www.gcheckin.com host
  // (in PROD AppMonitor domain_list) is NOT a brand — tag it "default" to keep
  // the SLI cohort clean.
  const host = normalizeHostname(hostname);
  const parts = host.split(".");
  if (parts.length === 3 && parts.slice(-2).join(".") === "gcheckin.com") {
    const first = parts[0];
    if (first === "www" || !first) return "default";
    return first;
  }
  return "default";
}

function isInstrumentableHost(hostname: string): boolean {
  // WHY: short-circuit on localhost / IP literals / non-gcheckin.com hosts so
  // local dev (vite at http://localhost:5173) does not pollute DEV RUM. The
  // AppMonitor's domain_list also excludes these — this is the SPA-side mirror.
  const host = normalizeHostname(hostname);
  if (host === "localhost" || host === "127.0.0.1" || host === "::1") return false;
  if (/^[0-9.]+$/.test(host)) return false;
  if (/^\[?[0-9a-fA-F:]+\]?$/.test(host) && host.includes(":")) return false;
  return host.endsWith("gcheckin.com");
}

function normalizePageRoute(pathname: string): string {
  // WHY: form publicIds and slug-history identifiers in path segments are quasi-identifiers
  // (LGPD scope for LATAM traffic) and would inflate CloudWatch metric cardinality without
  // bound. Route-template before emit. /f/ and /l/ are the short-form canonical paths
  // shipped by Story 32.9; /forms/, /form/, and /checkin/ are preserved as back-compat
  // aliases — all three are normalized here so legacy deep-link traffic does not emit
  // raw publicIds into RUM. The /preview/form/ normalization keeps preview-iframe traffic
  // from polluting the SLI cohort with raw preview identifiers.
  // Lowercased + trailing-slash-stripped so `/Forms/abc/` and `/forms/abc` land in the
  // same bucket.
  const normalized = pathname.toLowerCase().replace(/\/+$/, "") || "/";
  return normalized
    .replace(/^\/preview\/form\/[^/?#]+/, "/preview/form/:publicId")
    .replace(/^\/f\/[^/?#]+/, "/f/:publicId")
    .replace(/^\/l\/[^/?#]+/, "/l/:listingPublicId")
    .replace(/^\/forms\/[^/?#]+/, "/forms/:publicId")
    .replace(/^\/form\/[^/?#]+/, "/form/:publicId")
    .replace(/^\/checkin\/[^/?#]+/, "/checkin/:listingPublicId")
    .replace(/^\/v1\/[a-z]{2}\b/, "/v1/:lang");
}

function readConnectionEffectiveType(): string {
  // WHY: navigator.connection is Chromium-only. The "unknown" fallback lets the SLI
  // dashboard count Safari/Firefox sessions without dropping them. AC 8's alarm cohort
  // filter is EffectiveType IN ('4g','unknown') so iOS Safari users are included.
  const conn = (navigator as Navigator & { connection?: NetworkInformationLike }).connection;
  return conn?.effectiveType ?? "unknown";
}

function clearPendingTimeout(): void {
  if (pendingTimeout !== null) {
    window.clearTimeout(pendingTimeout);
    pendingTimeout = null;
  }
}

function disconnectObserver(): void {
  if (activeObserver !== null) {
    activeObserver.disconnect();
    activeObserver = null;
  }
}

export function registerBrandHydrationObserver(): void {
  if (registered) return;
  registered = true;

  if (typeof PerformanceObserver === "undefined") {
    return;
  }

  if (!isInstrumentableHost(window.location.hostname)) {
    return;
  }

  const draft: BrandHydrationEventDraft = { latencyMs: null, latencyMsPostMount: null };
  let emitted = false;

  function tryEmit(): void {
    if (emitted) return;
    if (draft.latencyMs === null) return; // SLI lens is load-bearing — never emit without it
    emitted = true;
    clearPendingTimeout();
    disconnectObserver();
    const rum = getAwsRum();
    if (rum === null) return;
    const event = {
      latencyMs: draft.latencyMs,
      // WHY -1 sentinel: if the post-mount measure never arrives (BrandingProvider
      // effect threw before performance.measure called), we still emit the SLI lens.
      // The metric extraction filter on BrandHydrationLatencyMsPostMount drops -1
      // sentinels via numeric >= 0 filter — see rum-metric-definitions.tf event_pattern.
      latencyMsPostMount: draft.latencyMsPostMount ?? -1,
      slug: extractSlug(window.location.hostname),
      pageRoute: normalizePageRoute(window.location.pathname),
      connectionEffectiveType: readConnectionEffectiveType(),
    };
    try {
      rum.recordEvent("brand_hydration_latency", event);
    } catch (err) {
      console.error("RUM brand_hydration_latency emit failed", err);
    }
  }

  // WHY synchronous probe: if marks/measures fired BEFORE this observer registered,
  // buffered:true only replays during the observer's subscription window. The pre-probe
  // catches that case.
  const preMark = performance.getEntriesByName("brand-applied")[0];
  if (preMark !== undefined && Number.isFinite(preMark.startTime) && preMark.startTime >= 0) {
    draft.latencyMs = Math.round(preMark.startTime);
  }
  const preMeasure = performance.getEntriesByName("brand-hydration-post-mount")[0];
  if (preMeasure !== undefined && Number.isFinite(preMeasure.duration) && preMeasure.duration >= 0) {
    draft.latencyMsPostMount = Math.round(preMeasure.duration);
  }
  tryEmit();

  if (emitted) return;

  activeObserver = new PerformanceObserver((entryList) => {
    for (const entry of entryList.getEntriesByName("brand-applied")) {
      if (draft.latencyMs === null && Number.isFinite(entry.startTime) && entry.startTime >= 0) {
        draft.latencyMs = Math.round(entry.startTime);
      }
    }
    for (const entry of entryList.getEntriesByName("brand-hydration-post-mount")) {
      if (draft.latencyMsPostMount === null && Number.isFinite(entry.duration) && entry.duration >= 0) {
        draft.latencyMsPostMount = Math.round(entry.duration);
      }
    }
    tryEmit();
  });

  try {
    activeObserver.observe({ entryTypes: ["mark", "measure"], buffered: true });
  } catch (err) {
    // WHY: Safari < 15 does not support buffered:true on mark observers. We swallow —
    // the SLI dashboard alarm cohort is EffectiveType IN ('4g','unknown'), so iOS Safari
    // still emits via the live observer path on Safari >= 14.
    console.warn("PerformanceObserver mark/measure with buffered:true unsupported", err);
  }

  // WHY read-timeout: covers the case where brand-applied DID fire but the post-mount
  // measure never followed (effect threw between the mark and the measure call). After
  // 1.5 s the timeout calls tryEmit with the nav-start latencyMs and a -1 sentinel for
  // post-mount. If brand-applied has not fired at all, tryEmit early-returns (SLI lens
  // is load-bearing) and the live observer keeps listening — a late brand-applied still
  // emits.
  pendingTimeout = window.setTimeout(tryEmit, READ_TIMEOUT_MS);
}

export function __resetObserverForTests(): void {
  if (import.meta.env.PROD) {
    throw new Error("__resetObserverForTests is not callable in production");
  }
  clearPendingTimeout();
  disconnectObserver();
  registered = false;
}
